// Lifecycle package builder (L1). Onboarding/offboarding checklists per
// employment category — seeded defaults apply until the tenant owner
// customizes (same philosophy as Document Requirements). Owners edit; other
// managers get a read-only view. Completion rules are derived from the
// package type (thin checklist, not a workflow engine).
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, ClipboardList, Loader2, PackageCheck, Pencil, Plus, RotateCcw,
  Save, ShieldAlert, Trash2, Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getApiErrorMessage } from '@/lib/apiError';
import { EMPLOYMENT_TYPE_KEYS } from '@/services/documentRequirementsService';
import {
  getLifecyclePackages, putLifecyclePackages, completionForType,
  assigneeRoleLabel, verifyKindLabel, exitTypeLabel,
  FALLBACK_VERIFY_KINDS, FALLBACK_EXIT_TYPES, ASSIGNEE_ROLE_ORDER,
  type LifecyclePackage, type LifecycleTaskDef, type LifecycleType,
} from '@/services/lifecycleService';

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

const deepCopy = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

/** Stable task key from a title (new tasks only — existing keys are kept). */
const slugKey = (title: string, taken: Set<string>): string => {
  const base = title.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'task';
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}_${n++}`;
  return key;
};

const inputCls = 'w-full h-9 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground';

const TYPE_BADGE: Record<string, string> = {
  onboarding: 'bg-success-soft text-success-strong border-transparent',
  offboarding: 'bg-danger-soft text-danger-strong border-transparent',
};

// ------------------------------------------------------------- editor dialog
const PackageEditorDialog: React.FC<{
  pkg: LifecyclePackage;
  isNew: boolean;
  readOnly: boolean;
  roles: string[];
  verifyKinds: string[];
  exitTypes: string[];
  employmentTypeLabel: Record<string, string>;
  onSave: (pkg: LifecyclePackage) => void;
  onClose: () => void;
}> = ({ pkg, isNew, readOnly, roles, verifyKinds, exitTypes, employmentTypeLabel, onSave, onClose }) => {
  const { t } = useTranslation('admin');
  const [draft, setDraft] = useState<LifecyclePackage>(() => deepCopy(pkg));

  const typeLabel: Record<LifecycleType, string> = {
    onboarding: t('lifecycle.onboarding', { defaultValue: 'Onboarding' }),
    offboarding: t('lifecycle.offboarding', { defaultValue: 'Offboarding' }),
  };

  const setType = (type: LifecycleType) =>
    // Completion rules follow the type — packages never invent their own transitions.
    // New offboarding packages start with an empty exit-type selection.
    setDraft((d) => ({
      ...d,
      type,
      completion: completionForType(type),
      ...(type === 'offboarding' ? { applies_to_exit_types: d.applies_to_exit_types ?? [] } : {}),
    }));

  const toggleAppliesTo = (key: string) =>
    setDraft((d) => ({
      ...d,
      applies_to_employment_types: d.applies_to_employment_types.includes(key)
        ? d.applies_to_employment_types.filter((k) => k !== key)
        : [...d.applies_to_employment_types, key],
    }));

  const toggleExitType = (key: string) =>
    setDraft((d) => {
      const cur = d.applies_to_exit_types ?? [];
      return {
        ...d,
        applies_to_exit_types: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
      };
    });

  const setTask = (i: number, patch: Partial<LifecycleTaskDef>) =>
    setDraft((d) => ({ ...d, tasks: d.tasks.map((task, idx) => (idx === i ? { ...task, ...patch } : task)) }));

  const moveTask = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const j = i + dir;
      if (j < 0 || j >= d.tasks.length) return d;
      const tasks = [...d.tasks];
      [tasks[i], tasks[j]] = [tasks[j], tasks[i]];
      return { ...d, tasks };
    });

  const removeTask = (i: number) =>
    setDraft((d) => ({ ...d, tasks: d.tasks.filter((_, idx) => idx !== i) }));

  const addTask = () =>
    setDraft((d) => ({
      ...d,
      tasks: [...d.tasks, { key: '', title: '', assignee_role: 'hr', required: true, verify: 'manual' }],
    }));

  const save = () => {
    if (!draft.name.trim()) {
      toast.error(t('lifecycle.name_required', { defaultValue: 'Give the package a name.' }));
      return;
    }
    if (draft.tasks.length === 0) {
      toast.error(t('lifecycle.tasks_required', { defaultValue: 'Add at least one task.' }));
      return;
    }
    if (draft.tasks.some((task) => !task.title.trim())) {
      toast.error(t('lifecycle.task_titles_required', { defaultValue: 'Every task needs a title.' }));
      return;
    }
    // Backfill keys for newly added tasks (existing keys are preserved).
    const taken = new Set(draft.tasks.map((task) => task.key).filter(Boolean));
    const tasks = draft.tasks.map((task) => {
      if (task.key) return { ...task, title: task.title.trim() };
      const key = slugKey(task.title, taken);
      taken.add(key);
      return { ...task, key, title: task.title.trim() };
    });
    onSave({ ...draft, name: draft.name.trim(), tasks });
  };

  const completionText = draft.type === 'offboarding'
    ? t('lifecycle.completion_off', { defaultValue: 'On completion: employee becomes Terminated + payroll disabled.' })
    : t('lifecycle.completion_on', { defaultValue: 'On completion: employee becomes Active + payroll enabled.' });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {readOnly
              ? t('lifecycle.view_package', { defaultValue: 'Package: {{name}}', name: pkg.name })
              : isNew
                ? t('lifecycle.new_package', { defaultValue: 'New package' })
                : t('lifecycle.edit_package', { defaultValue: 'Edit package' })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Name + type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="pkg-name">
                {t('lifecycle.pkg_name', { defaultValue: 'Package name' })}
              </label>
              <input
                id="pkg-name"
                className={inputCls}
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                disabled={readOnly}
                placeholder={t('lifecycle.pkg_name_ph', { defaultValue: 'e.g. Full-time onboarding' })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="pkg-type">
                {t('lifecycle.pkg_type', { defaultValue: 'Type' })}
              </label>
              <select
                id="pkg-type"
                className={inputCls}
                value={draft.type}
                onChange={(e) => setType(e.target.value as LifecycleType)}
                disabled={readOnly}
              >
                <option value="onboarding">{typeLabel.onboarding}</option>
                <option value="offboarding">{typeLabel.offboarding}</option>
              </select>
            </div>
          </div>

          {/* Applies-to: employment types (onboarding) or exit types (offboarding) */}
          {draft.type === 'offboarding' ? (
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">
                {t('lifecycle.applies_to_exits', { defaultValue: 'Applies to exit types' })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {exitTypes.map((key) => (
                  <label key={key} className={`flex items-center gap-2 text-sm ${readOnly ? '' : 'cursor-pointer'} text-foreground`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-orange-500 disabled:cursor-not-allowed"
                      checked={(draft.applies_to_exit_types ?? []).includes(key)}
                      onChange={() => toggleExitType(key)}
                      disabled={readOnly}
                    />
                    {exitTypeLabel(t, key)}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {t('lifecycle.applies_to_exits_hint', { defaultValue: 'Leave all unchecked to make this the fallback package for any exit type.' })}
              </p>
            </div>
          ) : (
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">
                {t('lifecycle.applies_to', { defaultValue: 'Applies to employment types' })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {EMPLOYMENT_TYPE_KEYS.map((key) => (
                  <label key={key} className={`flex items-center gap-2 text-sm ${readOnly ? '' : 'cursor-pointer'} text-foreground`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-orange-500 disabled:cursor-not-allowed"
                      checked={draft.applies_to_employment_types.includes(key)}
                      onChange={() => toggleAppliesTo(key)}
                      disabled={readOnly}
                    />
                    {employmentTypeLabel[key] || key}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                {t('lifecycle.applies_to_hint', { defaultValue: 'Leave all unchecked to make this the fallback package for any employment type.' })}
              </p>
            </div>
          )}

          {/* Tasks */}
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">{t('lifecycle.tasks', { defaultValue: 'Tasks' })}</p>
            <div className="space-y-2">
              {draft.tasks.map((task, i) => (
                <div key={`${task.key || 'new'}-${i}`} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      className={`${inputCls} flex-1`}
                      value={task.title}
                      onChange={(e) => setTask(i, { title: e.target.value })}
                      disabled={readOnly}
                      placeholder={t('lifecycle.task_title_ph', { defaultValue: 'Task title' })}
                      aria-label={t('lifecycle.task_title_ph', { defaultValue: 'Task title' })}
                    />
                    {!readOnly && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => moveTask(i, -1)} disabled={i === 0}
                          aria-label={t('lifecycle.move_up', { defaultValue: 'Move up' })}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => moveTask(i, 1)} disabled={i === draft.tasks.length - 1}
                          aria-label={t('lifecycle.move_down', { defaultValue: 'Move down' })}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-danger hover:bg-red-50 dark:hover:bg-red-500/10" onClick={() => removeTask(i)}
                          aria-label={t('lifecycle.remove_task', { defaultValue: 'Remove task' })}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-2 items-center">
                    <select
                      className={inputCls}
                      value={task.assignee_role}
                      onChange={(e) => setTask(i, { assignee_role: e.target.value })}
                      disabled={readOnly}
                      aria-label={t('lifecycle.task_role', { defaultValue: 'Assignee role' })}
                    >
                      {roles.map((r) => <option key={r} value={r}>{assigneeRoleLabel(r)}</option>)}
                    </select>
                    <select
                      className={inputCls}
                      value={task.verify}
                      onChange={(e) => setTask(i, { verify: e.target.value })}
                      disabled={readOnly}
                      aria-label={t('lifecycle.task_verify', { defaultValue: 'How the task is verified' })}
                    >
                      {verifyKinds.map((k) => <option key={k} value={k}>{verifyKindLabel(k)}</option>)}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-foreground justify-self-start sm:justify-self-end">
                      <Switch
                        checked={task.required}
                        onCheckedChange={(v) => setTask(i, { required: v })}
                        disabled={readOnly}
                        aria-label={t('lifecycle.task_required', { defaultValue: 'Required' })}
                      />
                      {t('lifecycle.task_required', { defaultValue: 'Required' })}
                    </label>
                  </div>
                </div>
              ))}
            </div>
            {!readOnly && (
              <Button variant="outline" size="sm" className="mt-2" onClick={addTask}>
                <Plus className="w-4 h-4 mr-1" /> {t('lifecycle.add_task', { defaultValue: 'Add task' })}
              </Button>
            )}
          </div>

          {/* Completion (derived from type — read-only by design) */}
          <div className="rounded-lg px-3 py-2 text-sm border bg-muted/50 border-border text-muted-foreground">
            {completionText}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {readOnly ? t('lifecycle.close', { defaultValue: 'Close' }) : t('lifecycle.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {!readOnly && (
            <Button onClick={save}>
              {t('lifecycle.apply', { defaultValue: 'Apply' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------------------- page
export const LifecyclePackagesPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const { isManager, isTenantOwner } = useRoleAccess();
  const canEdit = isTenantOwner;

  const [packages, setPackages] = useState<LifecyclePackage[]>([]);
  const [roles, setRoles] = useState<string[]>([...ASSIGNEE_ROLE_ORDER]);
  const [verifyKinds, setVerifyKinds] = useState<string[]>(FALLBACK_VERIFY_KINDS);
  const [exitTypes, setExitTypes] = useState<string[]>([...FALLBACK_EXIT_TYPES]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Editor: index into packages, or -1 for a new package; null = closed.
  const [editing, setEditing] = useState<number | null>(null);

  useEffect(() => {
    if (!isManager) { setLoading(false); return; }
    let alive = true;
    getLifecyclePackages()
      .then((res) => {
        if (!alive) return;
        // Seed the editor from the effective packages — a tenant that never
        // customized starts from the platform defaults.
        setPackages(deepCopy(res.packages || []));
        if (res.roles?.length) setRoles(res.roles);
        if (res.verify_kinds?.length) setVerifyKinds(res.verify_kinds);
        if (res.exit_types?.length) setExitTypes(res.exit_types);
      })
      .catch(() => { if (alive) setLoadError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isManager]);

  const employmentTypeLabel: Record<string, string> = useMemo(() => ({
    full_time: t('lifecycle.et_full_time', { defaultValue: 'Full-time' }),
    part_time: t('lifecycle.et_part_time', { defaultValue: 'Part-time' }),
    casual: t('lifecycle.et_casual', { defaultValue: 'Casual' }),
    contract: t('lifecycle.et_contract', { defaultValue: 'Contract' }),
    fixed_term: t('lifecycle.et_fixed_term', { defaultValue: 'Fixed-term' }),
    specific_task: t('lifecycle.et_specific_task', { defaultValue: 'Specific task (ELRA)' }),
    intern: t('lifecycle.et_intern', { defaultValue: 'Intern' }),
    seasonal: t('lifecycle.et_seasonal', { defaultValue: 'Seasonal' }),
    consultant: t('lifecycle.et_consultant', { defaultValue: 'Consultant' }),
    temporary: t('lifecycle.et_temporary', { defaultValue: 'Temporary' }),
    other: t('lifecycle.et_other', { defaultValue: 'Other' }),
  }), [t]);

  const typeLabel: Record<LifecycleType, string> = {
    onboarding: t('lifecycle.onboarding', { defaultValue: 'Onboarding' }),
    offboarding: t('lifecycle.offboarding', { defaultValue: 'Offboarding' }),
  };

  const applyFromResponse = (res: Awaited<ReturnType<typeof getLifecyclePackages>>) => {
    setPackages(deepCopy(res.packages || []));
    if (res.roles?.length) setRoles(res.roles);
    if (res.verify_kinds?.length) setVerifyKinds(res.verify_kinds);
    if (res.exit_types?.length) setExitTypes(res.exit_types);
  };

  const onSaveAll = async () => {
    setSaving(true);
    try {
      const res = await putLifecyclePackages(
        packages.map(({ custom: _custom, ...rest }) => rest),
      );
      applyFromResponse(res);
      setDirty(false);
      toast.success(t('lifecycle.saved', { defaultValue: 'Lifecycle packages saved.' }));
    } catch (e: any) {
      // 400 carries a validation message — surface it verbatim.
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.save_failed', { defaultValue: 'Could not save lifecycle packages.' }))));
    } finally {
      setSaving(false);
    }
  };

  const onResetDefaults = async () => {
    const ok = await confirm({
      title: t('lifecycle.reset_title', { defaultValue: 'Reset to defaults' }),
      message: t('lifecycle.reset_confirm', {
        defaultValue: 'Discard your custom packages and go back to the platform defaults? Running checklists keep their snapshot.',
      }),
      confirmLabel: t('lifecycle.reset', { defaultValue: 'Reset to defaults' }),
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const res = await putLifecyclePackages([]);
      applyFromResponse(res);
      setDirty(false);
      toast.success(t('lifecycle.reset_done', { defaultValue: 'Packages reset to the platform defaults.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.save_failed', { defaultValue: 'Could not save lifecycle packages.' }))));
    } finally {
      setSaving(false);
    }
  };

  const onRemovePackage = async (i: number) => {
    const ok = await confirm({
      title: t('lifecycle.remove_pkg_title', { defaultValue: 'Remove package' }),
      message: t('lifecycle.remove_pkg_confirm', { defaultValue: 'Remove “{{name}}” from the set? Save changes to apply.', name: packages[i]?.name }),
      confirmLabel: t('lifecycle.remove', { defaultValue: 'Remove' }),
      danger: true,
    });
    if (!ok) return;
    setPackages((cur) => cur.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const onEditorSave = (pkg: LifecyclePackage) => {
    setPackages((cur) => (editing === -1 ? [...cur, pkg] : cur.map((p, i) => (i === editing ? pkg : p))));
    setDirty(true);
    setEditing(null);
  };

  const newPackage = (): LifecyclePackage => ({
    name: '',
    type: 'onboarding',
    applies_to_employment_types: [],
    applies_to_exit_types: [],
    tasks: [],
    completion: completionForType('onboarding'),
    custom: true,
  });

  if (!isManager) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('lifecycle.admins_only', { defaultValue: 'Managers & admins only' })}</h1>
        <p className="text-muted-foreground mt-1">
          {t('lifecycle.no_permission_pkgs', { defaultValue: 'You do not have permission to view lifecycle packages.' })}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <PackageCheck className="w-6 h-6" /> {t('lifecycle.pkgs_title', { defaultValue: 'Lifecycle Packages' })}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('lifecycle.pkgs_subtitle', {
              defaultValue: 'Onboarding and offboarding checklists per employment category. Verified steps read live data; manual steps record a named confirmer.',
            })}
          </p>
        </div>
        {canEdit && !loading && !loadError && (
          <Button variant="outline" onClick={() => setEditing(-1)} disabled={saving}>
            <Plus className="w-4 h-4 mr-1" /> {t('lifecycle.new_package', { defaultValue: 'New package' })}
          </Button>
        )}
      </div>

      {!canEdit && !loading && !loadError && (
        <div className="rounded-lg px-3 py-2 text-sm border bg-warning-soft border-warning text-warning-strong">
          {t('lifecycle.read_only', { defaultValue: 'Read-only view — only the company owner can change lifecycle packages.' })}
        </div>
      )}

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          {t('lifecycle.loading_pkgs', { defaultValue: 'Loading lifecycle packages…' })}
        </div>
      ) : loadError ? (
        <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
          {t('lifecycle.load_error', { defaultValue: 'Could not load lifecycle packages.' })}
        </div>
      ) : (
        <>
          {packages.length === 0 ? (
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardContent className="py-12 text-center text-muted-foreground">
                {t('lifecycle.no_pkgs', { defaultValue: 'No packages defined. Save to keep it empty, or reset to the platform defaults.' })}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packages.map((p, i) => (
                <Card key={`${p.name}-${i}`} className="border-orange-100 dark:border-orange-500/20">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground flex items-center gap-2 min-w-0">
                        <ClipboardList className="w-4 h-4 text-orange-500 shrink-0" />
                        <span className="truncate">{p.name || t('lifecycle.unnamed', { defaultValue: '(unnamed)' })}</span>
                      </p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge className={`border ${TYPE_BADGE[p.type] || TYPE_BADGE.onboarding}`}>{typeLabel[p.type] || p.type}</Badge>
                        <Badge className={`border ${p.custom
                          ? 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-500/25'
                          : 'bg-muted text-muted-foreground border-border'}`}>
                          {p.custom
                            ? t('lifecycle.badge_custom', { defaultValue: 'Custom' })
                            : t('lifecycle.badge_default', { defaultValue: 'Default' })}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {p.type === 'offboarding' ? (
                        (p.applies_to_exit_types ?? []).length === 0 ? (
                          <span className="text-xs text-muted-foreground border border-dashed border-border rounded-full px-2 py-0.5">
                            {t('lifecycle.applies_any_exit', { defaultValue: 'Any exit type (fallback)' })}
                          </span>
                        ) : (p.applies_to_exit_types ?? []).map((et) => (
                          <span key={et} className="text-xs text-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                            {exitTypeLabel(t, et)}
                          </span>
                        ))
                      ) : p.applies_to_employment_types.length === 0 ? (
                        <span className="text-xs text-muted-foreground border border-dashed border-border rounded-full px-2 py-0.5">
                          {t('lifecycle.applies_any', { defaultValue: 'Any employment type (fallback)' })}
                        </span>
                      ) : p.applies_to_employment_types.map((et) => (
                        <span key={et} className="text-xs text-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                          {employmentTypeLabel[et] || et}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        {t('lifecycle.task_count', { defaultValue: '{{count}} tasks', count: p.tasks.length })}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(i)} disabled={saving}>
                          {canEdit
                            ? <><Pencil className="w-3.5 h-3.5 mr-1" /> {t('lifecycle.edit', { defaultValue: 'Edit' })}</>
                            : <><Eye className="w-3.5 h-3.5 mr-1" /> {t('lifecycle.view', { defaultValue: 'View' })}</>}
                        </Button>
                        {canEdit && (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-danger hover:bg-red-50 dark:hover:bg-red-500/10"
                            onClick={() => onRemovePackage(i)} disabled={saving}
                            aria-label={t('lifecycle.remove', { defaultValue: 'Remove' })}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={onSaveAll} disabled={saving || !dirty}>
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {saving
                  ? t('lifecycle.saving', { defaultValue: 'Saving…' })
                  : t('lifecycle.save', { defaultValue: 'Save changes' })}
              </Button>
              <Button variant="outline" onClick={onResetDefaults} disabled={saving}>
                <RotateCcw className="w-4 h-4 mr-1" />
                {t('lifecycle.reset', { defaultValue: 'Reset to defaults' })}
              </Button>
              {dirty && (
                <span className="text-xs text-muted-foreground">
                  {t('lifecycle.unsaved', { defaultValue: 'Unsaved changes.' })}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {editing !== null && (
        <PackageEditorDialog
          pkg={editing === -1 ? newPackage() : packages[editing]}
          isNew={editing === -1}
          readOnly={!canEdit}
          roles={roles}
          verifyKinds={verifyKinds}
          exitTypes={exitTypes}
          employmentTypeLabel={employmentTypeLabel}
          onSave={onEditorSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default LifecyclePackagesPage;
